import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription, debounceTime } from 'rxjs';
import { lastValueFrom, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { EwsDataService, dateOnly, monthLabel } from '../../../../../services/ews-data.service';
import { downloadCsv } from '../../../../../services/swap-data.service';

// =====================================================================
// EwsHistoryComponent — Scope 6.4 "EWS Saved Forecasts" screen
// ---------------------------------------------------------------------
// Tab 1 · Saved forecasts: every saved run (EWS / Bean Logic / Proposal),
//         newest first. Click a run name → its assumptions, warehouse
//         summary and month-by-month numbers exactly as they were saved.
// Tab 2 · Audit history: who changed what (customer inputs, notes, saved
//         forecasts, settings) with before / after values, CSV export.
// Data: GET /api/v1/ews/runs · /runs/:RunID · /audit
// =====================================================================

@Component({
  selector: 'app-ews-history',
  imports: [RmsModule],
  templateUrl: './ews-history.component.html',
  styleUrl: './ews-history.component.scss'
})
export class EwsHistoryComponent implements OnInit, OnDestroy {
  private live?: Subscription;
  ngOnDestroy() { this.live?.unsubscribe(); }
  // ---------------- saved forecasts ----------------
  LoadingRuns = false;
  RunType: string | null = null;
  RunTypeOptions = [
    { value: 'EWS', label: 'EWS group analysis' },
    { value: 'BEAN_LOGIC', label: 'Bean Logic' },
    { value: 'PROPOSAL', label: 'Purchase proposal' }
  ];
  Runs: any[] = [];
  RunColumns: Column[] = [
    { key: 'RunID', label: 'Run #', type: 'number', hidden: false, width: 5 },
    {
      key: 'RunName', label: 'Name', hidden: false, align: 'left', width: 22, clickable: true,
      onAction: (row: any) => { this.openRun(row); }
    },
    { key: 'RunTypeText', label: 'Type', type: 'select', SearchType: 'select', hidden: false },
    { key: 'VersionNo', label: 'Version', type: 'number', hidden: false, width: 5 },
    { key: 'Scope', label: 'Groups / Customer', hidden: false, align: 'left', width: 22 },
    { key: 'AsOfDate', label: 'Burn to', type: 'date', SearchType: 'date', hidden: false },
    { key: 'BurnText', label: 'Burn', hidden: false },
    { key: 'SafsText', label: 'SAFS', hidden: false },
    { key: 'LineCount', label: 'Lines', type: 'number', hidden: false },
    { key: 'SavedText', label: 'Saved', hidden: false },
    { key: 'GeneratedByName', label: 'Saved by', type: 'select', SearchType: 'select', hidden: false },
    { key: 'Notes', label: 'Notes', hidden: true },
  ];
  RunButtons: HeaderButton[] = [{ label: 'Export CSV', onAction: () => downloadCsv(this.Runs, `ews-saved-forecasts-${today()}.csv`) }];

  // ---- one run opened ----
  LoadingRun = false;
  Run: any = null;
  Assumptions: { label: string; value: string }[] = [];
  SummaryLines: any[] = [];
  RunWarehouses: { key: string; name: string }[] = [];
  SelectedRunWh = 'ALL';
  MonthLines: any[] = [];                 // MONTH lines of the selected warehouse
  OtherLines: any[] = [];                 // PROPOSAL / RECOMMENDATION lines
  OtherColumns: Column[] = [
    { key: 'LineType', label: 'Line', type: 'select', SearchType: 'select', hidden: false },
    { key: 'GroupName', label: 'Group', hidden: false, align: 'left' },
    { key: 'InventoryTypeName', label: 'SKU', hidden: false, align: 'left', width: 22 },
    { key: 'WarehouseText', label: 'Warehouse', hidden: false },
    { key: 'BurnRate', label: 'Burn', type: 'number', hidden: false },
    { key: 'RecommendedBags', label: 'Recommended', type: 'number', hidden: false },
    { key: 'ProposedBags', label: 'Proposed', type: 'number', hidden: false },
    { key: 'LineNote', label: 'Note', hidden: false, align: 'left', width: 20 },
  ];
  private AllLines: any[] = [];

  // ---------------- audit history ----------------
  LoadingAudit = false;
  AuditLoaded = false;
  AuditFrom: string = daysAgo(30);
  AuditTo: string = today();
  AuditEntity: string | null = null;
  EntityOptions = [
    { value: 'FORECAST_RUN', label: 'Saved forecasts' },
    { value: 'NOTE', label: 'Notes' },
    { value: 'CUSTOMER_INPUT', label: 'Bean Logic customer inputs' },
    { value: 'SETTING', label: 'Settings' }
  ];
  Audit: any[] = [];
  AuditColumns: Column[] = [
    { key: 'WhenText', label: 'When', hidden: false, width: 9 },
    { key: 'ActionByName', label: 'By', type: 'select', SearchType: 'select', hidden: false },
    { key: 'ActionType', label: 'Action', type: 'select', SearchType: 'select', hidden: false },
    { key: 'EntityText', label: 'What', type: 'select', SearchType: 'select', hidden: false },
    { key: 'GroupName', label: 'Group', hidden: false, align: 'left' },
    { key: 'CompanyName', label: 'Customer', hidden: false, align: 'left' },
    { key: 'ActionDetail', label: 'Detail', hidden: false, align: 'left', width: 26 },
    { key: 'RunID', label: 'Run #', type: 'number', hidden: true },
    { key: 'BeforeValue', label: 'Before', hidden: true, align: 'left' },
    { key: 'AfterValue', label: 'After', hidden: true, align: 'left' },
  ];
  AuditButtons: HeaderButton[] = [{ label: 'Export CSV', onAction: () => downloadCsv(this.Audit, `ews-audit-${this.AuditFrom}-to-${this.AuditTo}.csv`) }];

  constructor(
    private ews: EwsDataService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    await this.loadRuns();
    this.live = this.ews.changes$.pipe(debounceTime(400)).subscribe(() => {
      if (!this.LoadingRuns) this.loadRuns();
      if (this.AuditLoaded && !this.LoadingAudit) this.loadAudit();
    });
  }

  // ---------------------------------------------------------------------
  // Saved forecasts
  // ---------------------------------------------------------------------

  async loadRuns() {
    this.LoadingRuns = true;
    this.cdr.markForCheck();
    try {
      const rows = await lastValueFrom(this.ews.getRuns({ RunType: (this.RunType || undefined) as any, Top: 500 })) ?? [];
      this.Runs = rows.map((r: any) => ({
        ...r,
        RunTypeText: runTypeText(r.RunType),
        Scope: r.CompanyName || r.GroupNames || '—',
        AsOfDate: dateOnly(r.AsOfDate),
        BurnText: r.BurnRange ? `${r.BurnRange} mo` : '',
        // SAFS only means something for EWS runs (Bean Logic / proposals don't use it)
        SafsText: r.RunType !== 'EWS' || r.SafsValue === null || r.SafsValue === undefined ? ''
          : `${r.SafsValue}${r.SafsType === 'BAGS' ? ' bags' : '%'}`,
        SavedText: wallClock(r.GeneratedDate)
      }));
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the saved forecasts.');
    } finally {
      this.LoadingRuns = false;
      this.cdr.markForCheck();
    }
  }

  async openRun(row: any) {
    this.LoadingRun = true;
    this.cdr.markForCheck();
    try {
      const d = await lastValueFrom(this.ews.getRun(row.RunID));
      this.Run = d?.Run ?? null;
      this.AllLines = (d?.Lines ?? []).map((l: any) => ({
        ...l,
        WarehouseText: whName(l.WarehouseID, this.SummaryLines),
        MonthStart: dateOnly(l.MonthStart)
      }));
      this.buildRunView();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not open that saved forecast.');
    } finally {
      this.LoadingRun = false;
      this.cdr.markForCheck();
    }
  }

  closeRun() {
    this.Run = null;
    this.AllLines = [];
  }

  private buildRunView() {
    const r = this.Run;
    if (!r) return;

    // assumptions = filter values + whatever the screen saved in AssumptionsJson
    let extra: Record<string, any> = {};
    try { extra = r.AssumptionsJson ? JSON.parse(r.AssumptionsJson) : {}; } catch { extra = {}; }
    const list = (json: any) => { try { const a = JSON.parse(json); return Array.isArray(a) && a.length ? a.join(', ') : ''; } catch { return ''; } };

    this.Assumptions = [
      { label: 'Type', value: runTypeText(r.RunType) },
      { label: 'Version', value: `v${r.VersionNo}${r.LatestVersionNo && r.LatestVersionNo !== r.VersionNo ? ` (latest is v${r.LatestVersionNo})` : ' (latest)'}` },
      { label: 'Groups', value: r.GroupNames || '—' },
      { label: 'Customer', value: r.CompanyName || '—' },
      { label: 'Burn', value: `${r.BurnRange ?? '—'}-month average to ${dateOnly(r.AsOfDate) ?? '—'}` },
      { label: 'SAFS', value: r.SafsValue === null || r.SafsValue === undefined ? '—' : `${r.SafsValue}${r.SafsType === 'BAGS' ? ' bags / month' : '%'}` },
      { label: 'Crop cycles', value: r.CropOption === 1 ? 'Crop #1 only' : r.CropOption === 2 ? 'Crop #1 + #2' : '—' },
      { label: 'Volatile market', value: r.VolatileMarket ? 'Yes' : 'No' },
      { label: 'Exclude fire sales', value: r.ExcludeFireSales ? 'Yes' : 'No' },
      { label: 'Days-in adj.', value: String(r.DaysInAdj ?? 0) },
      { label: 'Excluded customers', value: list(r.ExcludeCustomersJson) || 'none' },
      { label: 'Excluded SKUs', value: list(r.ExcludeSKUsJson) || 'none' },
      { label: 'Included SKUs', value: list(r.IncludeSKUsJson) || 'none' },
      ...Object.entries(extra).map(([k, v]) => ({ label: splitWords(k), value: v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v) })),
      { label: 'Saved', value: `${wallClock(r.GeneratedDate)} by ${r.GeneratedByName ?? 'unknown'}` }
    ];

    this.SummaryLines = this.AllLines.filter(l => l.LineType === 'SUMMARY');
    this.AllLines.forEach(l => l.WarehouseText = whName(l.WarehouseID, this.SummaryLines));
    this.OtherLines = this.AllLines.filter(l => l.LineType === 'PROPOSAL' || l.LineType === 'RECOMMENDATION');

    const months = this.AllLines.filter(l => l.LineType === 'MONTH');
    const keys = [...new Set(months.map(l => whKey(l.WarehouseID)))];
    this.RunWarehouses = keys.map(k => ({ key: k, name: whName(k === 'ALL' ? null : Number(k), this.SummaryLines) }));
    this.selectRunWh(keys.includes('ALL') ? 'ALL' : keys[0] ?? 'ALL');
  }

  selectRunWh(key: string) {
    this.SelectedRunWh = key;
    this.MonthLines = this.AllLines
      .filter(l => l.LineType === 'MONTH' && whKey(l.WarehouseID) === key)
      .sort((a, b) => String(a.MonthStart).localeCompare(String(b.MonthStart)))
      .map(l => ({ ...l, Label: monthLabel(l.MonthStart) }));
    this.cdr.markForCheck();
  }

  exportRunLines() {
    if (!this.Run) return;
    downloadCsv(this.AllLines, `ews-run-${this.Run.RunID}-v${this.Run.VersionNo}.csv`);
  }

  // ---------------------------------------------------------------------
  // Audit history
  // ---------------------------------------------------------------------

  async loadAudit() {
    if (this.AuditFrom && this.AuditTo && this.AuditFrom > this.AuditTo) {
      this.msg.warn('From date must be on or before the to date.');
      return;
    }
    this.LoadingAudit = true;
    this.cdr.markForCheck();
    try {
      const rows = await lastValueFrom(this.ews.getAudit({
        FromDate: this.AuditFrom || undefined,
        ToDate: this.AuditTo || undefined,
        EntityType: this.AuditEntity || undefined,
        Top: 2000
      })) ?? [];
      this.Audit = rows.map((h: any) => ({
        ...h,
        WhenText: wallClock(h.ActionDate),
        EntityText: entityText(h.EntityType)
      }));
      this.AuditLoaded = true;
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the audit history.');
    } finally {
      this.LoadingAudit = false;
      this.cdr.markForCheck();
    }
  }

  d(v: any): string | null { return dateOnly(v); }
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function today(): string {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

function daysAgo(days: number): string {
  const n = new Date();
  n.setDate(n.getDate() - days);
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

function pad(n: number): string {
  return n < 10 ? '0' + n : String(n);
}

/** SQL datetime is New York time but arrives tagged UTC → show the wall clock as stored */
function wallClock(v: any): string {
  if (!v) return '';
  const s = String(v);
  return `${s.substring(0, 10)} ${s.substring(11, 16)}`;
}

function whKey(id: any): string {
  return id === null || id === undefined ? 'ALL' : String(id);
}

/** warehouse name from the SUMMARY lines' notes ("RNY-SouthPnfld: Covered; N/A") */
function whName(id: any, summary: any[]): string {
  if (id === null || id === undefined || id === 'ALL') return 'All warehouses';
  const s = summary.find(l => l.WarehouseID === Number(id));
  const name = s?.LineNote ? String(s.LineNote).split(':')[0] : '';
  return name || (Number(id) === -1 ? 'Other' : `Warehouse ${id}`);
}

function runTypeText(t: string): string {
  switch (t) {
    case 'EWS': return 'EWS group analysis';
    case 'BEAN_LOGIC': return 'Bean Logic';
    case 'PROPOSAL': return 'Purchase proposal';
    default: return t ?? '';
  }
}

function entityText(t: string): string {
  switch (t) {
    case 'FORECAST_RUN': return 'Saved forecast';
    case 'NOTE': return 'Note';
    case 'CUSTOMER_INPUT': return 'Customer input';
    case 'SETTING': return 'Setting';
    default: return t ?? '';
  }
}

/** "AvgDaysToWarehouse" → "Avg days to warehouse" */
function splitWords(k: string): string {
  const s = k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
